// Auto-generated monthly report document shell. Replace TODO narrative before publishing.
import { defineDocument, em, link, list, p, section, table, text, } from '../../schema';
export const report202606TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-06-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-06-top-party-games-and-streamers',
    route: '/party-central/reports/2026-06-top-party-games-and-streamers',
    seo: {
        title: 'June 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for June 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'June 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'emerald',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-07-06T00:00:00.000Z'),
        updatedAt: new Date('2026-07-06T00:00:00.000Z'),
        reportMonth: '2026-06',
        dataRange: {
            start: '2026-06-01',
            end: '2026-06-30',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('In June we tracked 177,900 party game streams from nearly 74,000 streamers across 950 games and 34 languages. See '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' for collection details.'),
                ]),
                p([
                    text("The month's defining story was "),
                    em('Meccha Chameleon'),
                    text(', a Japanese indie hide-and-seek game that '),
                    link('https://store.steampowered.com/app/4704690/MECCHA_CHAMELEON/', 'launched on Steam'),
                    text(" on June 10 and became 2026's biggest breakout, selling over 10 million copies within a few weeks and exploding on Twitch to become one of the most popular things to stream. To put the scale in plain terms: from the moment we started tracking the game, it made up more than half of every party game stream we captured with nearly twelve times the volume of the next most-streamed game and more than the entire rest of the top ten combined. Our capture of it only began in the final two days of June and it still became the most widely-streamed game of the month. Those totals understate its true footprint and its phenomenal growth is unlike anything we've seen before."),
                ]),
                p([
                    text('Keep in mind the limited capture window as you read many of the figures in this report. For example, overall stream volume appears 4.1% lower than May, but because we missed a significant shift in party-game activity towards '),
                    em('Meccha Chameleon'),
                    text(' during the first few weeks of its release, real party-game activity in June very likely increased due to the rapid uptake of the game by streamers.'),
                ]),
                p([
                    text('Besides the breakout story, '),
                    em('nanos world'),
                    text(' again held one of the strongest Party Scores. Its lead on other games remains a story of the dedicated French community playing it: 97% of its streams came from French channels and its stickiness climbed even higher, from 13.1 to 14.9 streams per streamer. There is a dedicated cohort of about 150 streamers playing heavily, with creators like '),
                    link('https://twitch.tv/miidouw_', 'Miidouw_'),
                    text(' driving a large portion of its viewership.'),
                ]),
                p([
                    link('https://twitch.tv/dumbdog', 'DumbDog'),
                    text(' took the top Party Score for streamers, rotating a large audience across seven party games.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' topped the list again this month; its 14.9 stickiness is the highest of any game, and it comes from a narrow French community treating the sandbox platform as a daily habit. Nearly all of its streams are French, and its 156 creators average roughly 15 streams each.'),
                        ]),
                        p([
                            text('Four names entered the top 15 that were absent in May: '),
                            em('Pinball'),
                            text(', '),
                            em('Gartic Phone'),
                            text(', '),
                            em('UNO'),
                            text(', and '),
                            em('The House Always Wins'),
                            text('. '),
                            em('Pinball'),
                            text(' is the most interesting of them, reaching #2 on a 10.5 stickiness driven by arcade and venue channels such as '),
                            link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                            text(' that broadcast physical machines for hours at a time.'),
                        ]),
                        p([
                            text('While the average viewership for '),
                            em('The House Always Wins'),
                            text(` towered above other games at 215, its stickiness is low, marking shallow one-time plays by creators with big follower counts rather than long-term engagement. This is a great example of how Party Score normalizes a game's true popularity rather than allowing a few big streamers to skew the results. Several games cooled month-over-month, with `),
                            em('Words On Stream'),
                            text(', '),
                            em('Just Dance 2026 Edition'),
                            text(', and '),
                            em('Super Smash Bros. Ultimate'),
                            text(' each sliding six places as their stream and viewer counts fell.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'nanos world', '95', '2,328', '156', '14.9', '53'],
                                ['2', 'Pinball', '93', '1,751', '166', '10.5', '28'],
                                ['3', 'Gartic Phone', '92', '9,139', '2,871', '3.2', '18'],
                                ['4', 'Mahjong Soul', '92', '4,097', '1,448', '2.8', '23'],
                                ['5', 'UNO', '92', '1,858', '526', '3.5', '22'],
                                ['6', 'Fall Guys', '91', '11,726', '5,766', '2.0', '30'],
                                [
                                    '7',
                                    'Diablo II: Resurrected',
                                    '91',
                                    '6,923',
                                    '1,129',
                                    '6.1',
                                    '13',
                                ],
                                ['8', 'Words On Stream', '91', '7,041', '587', '12.0', '14'],
                                [
                                    '9',
                                    'Just Dance 2026 Edition',
                                    '91',
                                    '493',
                                    '207',
                                    '2.4',
                                    '52',
                                ],
                                [
                                    '10',
                                    'Super Smash Bros. Ultimate',
                                    '90',
                                    '11,318',
                                    '3,423',
                                    '3.3',
                                    '13',
                                ],
                                [
                                    '11',
                                    'Mario Kart World',
                                    '90',
                                    '5,456',
                                    '2,213',
                                    '2.5',
                                    '22',
                                ],
                                [
                                    '12',
                                    'Granblue Fantasy Versus: Rising',
                                    '90',
                                    '1,089',
                                    '473',
                                    '2.3',
                                    '25',
                                ],
                                ['13', 'Mario Kart 64', '90', '283', '102', '2.8', '31'],
                                [
                                    '14',
                                    'Jackbox Party Packs',
                                    '89',
                                    '3,950',
                                    '1,930',
                                    '2.0',
                                    '21',
                                ],
                                [
                                    '15',
                                    'The House Always Wins',
                                    '89',
                                    '758',
                                    '472',
                                    '1.6',
                                    '215',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/dumbdog', 'DumbDog'),
                            text(' led as a new entrant on the Party Score leaderboard by doing exactly what the score rewards: showing up often, across several games, with a large audience each time. We captured them 19 times across seven titles, anchored by '),
                            em('Mahjong Soul'),
                            text(' but including '),
                            em('Among Us'),
                            text(' sessions that averaged over 1,200 viewers.'),
                        ]),
                        p([
                            text('Just beneath the top spot, '),
                            link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                            text(' climbed to second after expanding their game repertoire and lifting their average audience from 335 to 379, with their biggest draws coming on '),
                            em('Town of Salem 2'),
                            text(' and a pair of '),
                            em('Meccha Chameleon'),
                            text(' sessions. New arrival to the list, '),
                            link('https://twitch.tv/feinekatze', 'FeineKatze'),
                            text(', went from streaming only '),
                            em('Among Us'),
                            text(' in May to a steadier three-game slate in June, adding '),
                            em('Codenames'),
                            text(' and '),
                            em('Gartic Phone'),
                            text(' while holding a 400-plus average. Another newcomer to the list, '),
                            link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                            text(', increased their average audience from about 200 last month to over 300 this month across seven titles. '),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/dumbdog', 'DumbDog')],
                                    '98',
                                    '19',
                                    '713',
                                    '7',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '98',
                                    '17',
                                    '379',
                                    '6',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/buni', 'Buni')],
                                    '98',
                                    '23',
                                    '243',
                                    '3',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '98',
                                    '18',
                                    '235',
                                    '10',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '98',
                                    '24',
                                    '193',
                                    '6',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/zeroyalviking', 'ZeRoyalViking')],
                                    '97',
                                    '12',
                                    '488',
                                    '6',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/feinekatze', 'FeineKatze')],
                                    '97',
                                    '15',
                                    '417',
                                    '3',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                                    ],
                                    '97',
                                    '13',
                                    '313',
                                    '7',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/swiftor', 'Swiftor')],
                                    '97',
                                    '22',
                                    '214',
                                    '3',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '97',
                                    '21',
                                    '203',
                                    '3',
                                ],
                                [
                                    '11',
                                    [link('https://twitch.tv/x33n', 'X33N')],
                                    '97',
                                    '18',
                                    '193',
                                    '4',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/getonmylvltv', 'GetOnMyLvLTV')],
                                    '97',
                                    '18',
                                    '120',
                                    '6',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/vanilian', 'Vanilian')],
                                    '97',
                                    '44',
                                    '82',
                                    '3',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/cpxtrey', 'CPxTrey')],
                                    '97',
                                    '55',
                                    '71',
                                    '4',
                                ],
                                [
                                    '15',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '97',
                                    '85',
                                    '66',
                                    '10',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The volume leaders are the familiar social staples, but the most interesting story is the sudden rise of '),
                            em('Meccha Chameleon'),
                            text(', which ranked fourth in total streams for June despite being captured only over the last two days of the month. The daily average is well ahead of anything else on this table and, had we captured more than two days of activity, it would have easily been the top game of the month. '),
                            em('Super Smash Bros. Ultimate'),
                            text(' and '),
                            em('Diablo II: Resurrected'),
                            text(' both softened from May, the latter shedding nearly a quarter of its streams as its creator base thinned.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Among Us', '13,847', '8,182', '18'],
                                ['2', 'Fall Guys', '11,726', '5,766', '30'],
                                ['3', 'Super Smash Bros. Ultimate', '11,318', '3,423', '13'],
                                ['4', 'MECCHA CHAMELEON', '10,395', '8,518', '57'],
                                ['5', 'Gartic Phone', '9,139', '2,871', '18'],
                                ['6', 'Words On Stream', '7,041', '587', '14'],
                                ['7', 'Lethal Company', '6,959', '3,884', '11'],
                                ['8', 'Diablo II: Resurrected', '6,923', '1,129', '13'],
                                ['9', 'Mario Kart World', '5,456', '2,213', '22'],
                                ['10', 'Mahjong Soul', '4,097', '1,448', '23'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Maitre des Fleurs'),
                            text(' tops the table, but the French variety streamer '),
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(" produced virtually all of the game's total viewership across a handful of streams. Setting that aside, the recurring pattern is that hosted game-show formats gather large audiences on modest stream counts: "),
                            em('Family Feud'),
                            text(', '),
                            em('Who Wants to Be a Millionaire'),
                            text(', and '),
                            em('Clubhouse Games: 51 Worldwide Classics'),
                            text(" all drew heavily on relatively few streams. Most heavily-streamed games don't make this list because more streamers means closer-to-average viewership per stream; however, "),
                            em("Liar's Bar"),
                            text(' cracks the list this month largely on the back of '),
                            link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                            text(' who averaged over 38,000 viewers across two streams of the game.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Maitre des Fleurs', '1,037', '20', '7'],
                                ['2', 'Family Feud', '941', '56', '45'],
                                ['3', 'Who Wants to Be a Millionaire', '843', '32', '29'],
                                [
                                    '4',
                                    'Clubhouse Games: 51 Worldwide Classics',
                                    '629',
                                    '40',
                                    '39',
                                ],
                                ['5', 'Trail & Error', '410', '80', '58'],
                                ['6', 'Wii Party', '391', '53', '39'],
                                ['7', "Liar's Bar", '268', '658', '532'],
                                ['8', 'Party Quiz', '264', '14', '11'],
                                [
                                    '9',
                                    'Itadaki Street: Dragon Quest & Final Fantasy 30th Anniversary',
                                    '258',
                                    '12',
                                    '6',
                                ],
                                ['10', 'Super Monkey Ball', '216', '27', '24'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Meccha Chameleon'),
                            text(' reached the widest creator base of any game, past even '),
                            em('Among Us'),
                            text(', and it did so in only two days of capture at the end of the month. The pickup was broad in every sense: more than 8,500 creators spread across 32 languages. This is a global surge of simultaneous adoption. Its dominance over those two days was near-total: of the roughly 20,000 party game streams we captured on June 29 and 30, Meccha Chameleon was 10,395 of them, out-streaming the next nine widely-streamed games combined. '),
                            em('Super Battle Golf'),
                            text(' saw its momentum continue to cool, still sixth by breadth but with a streamer base down nearly 40% from May.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'MECCHA CHAMELEON', '8,518', '10,395'],
                                ['2', 'Among Us', '8,182', '13,847'],
                                ['3', 'Fall Guys', '5,766', '11,726'],
                                ['4', 'Lethal Company', '3,884', '6,959'],
                                ['5', 'Super Smash Bros. Ultimate', '3,423', '11,318'],
                                ['6', 'Super Battle Golf', '2,998', '3,738'],
                                ['7', 'Gartic Phone', '2,871', '9,139'],
                                ['8', 'Mario Kart World', '2,213', '5,456'],
                                ['9', 'Golf With Your Friends', '2,180', '3,156'],
                                ['10', 'Jackbox Party Packs', '1,930', '3,950'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' again leads the stickiest games this month with its repeat-heavy French community, but games like '),
                            em('Debate'),
                            text(' earns its stickiness from a genuinely spread-out cohort of talk-format streamers across multiple languages. '),
                            em('Worms Armageddon'),
                            text(', the 1999 artillery classic, sits fifth almost entirely on one channel: '),
                            link('https://twitch.tv/wormsdirector', 'WormsDirector'),
                            text(', who is running competitive replays on a loop for a small audience of loyal viewers.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'nanos world', '14.9', '2,328', '156'],
                                ['2', 'Words On Stream', '12.0', '7,041', '587'],
                                ['3', 'Pinball', '10.5', '1,751', '166'],
                                ['4', 'Ragnarok X: Next Generation', '10.1', '384', '38'],
                                ['5', 'Worms Armageddon', '9.2', '276', '30'],
                                ['6', 'Debate', '6.9', '187', '27'],
                                ['7', 'Diablo II: Resurrected', '6.1', '6,923', '1,129'],
                                [
                                    '8',
                                    'The Legend of Heroes: Trails of Cold Steel',
                                    '6.0',
                                    '293',
                                    '49',
                                ],
                                [
                                    '9',
                                    'The Legend of Heroes: Trails of Cold Steel II',
                                    '6.0',
                                    '120',
                                    '20',
                                ],
                                ['10', 'Mabinogi', '5.8', '1,030', '179'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Meccha Chameleon'),
                            text(' was the runaway debut of the month, and likely will be the biggest viral sensation of the whole year. The '),
                            link('https://store.steampowered.com/app/4704690/MECCHA_CHAMELEON/', 'self-published Japanese indie'),
                            text(' sold its first million copies within four days of its June 10 launch and has now passed 15 million at the time of this writing. It has been the fastest and best-selling game of 2026 with only word-of-mouth marketing. '),
                        ]),
                        p([
                            text('While tough to compare to the massive success of '),
                            em('Meccha Chameleon'),
                            text(', '),
                            em('Trail & Error'),
                            text(' also debuted this month with a strong average of 410 viewers across 80 streams, largely concentrated in the Portuguese community.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['Meccha Chameleon', 'Jun 9', '10,395', '8,518', '57'],
                                ['Trail & Error', 'Jun 16', '80', '58', '410'],
                                [
                                    'Monopoly: Star Wars Heroes vs. Villains',
                                    'Jun 30',
                                    '34',
                                    '28',
                                    '21',
                                ],
                                ['Banana of Doom', 'Jun 2', '28', '25', '35'],
                                [
                                    'Masters of the Universe: Legends Unite',
                                    'Jun 5',
                                    '5',
                                    '4',
                                    '2',
                                ],
                                ['Night Club Simulator: Party King', 'Jun 30', '3', '2', '1'],
                                ['Busy Bar: World Tour', 'Jun 23', '1', '1', '9'],
                                ['Reloadian', 'Jun 19', '1', '1', '1'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('There were 33 other party games launched in June without observed Twitch activity. We list them below to bring awareness to these new games and help streamers discover something new.'),
                        list({
                            items: [
                                'Damashi Game (Jun 1)',
                                'Brain Training! Order Quiz (Jun 4)',
                                'Aux Court (Jun 8)',
                                'Silent Howl (Jun 9)',
                                'Instant Family Fun Bundle (Jun 11)',
                                'Maximum Sports Silver Collection (Jun 11)',
                                'Reel it! World Fishing (Jun 11)',
                                'Road to Jonin! The 3-Choice Ninja Quiz (Jun 11)',
                                'Spot the Impossible with Common Sense! (Jun 11)',
                                'Zumba: Jurassic Marble Quest (Jun 11)',
                                'Meccha Chameleon: CS2 Mirage (Jun 14)',
                                'Hotseat Wagons 2: Space World (Jun 15)',
                                'Vibe or Dare (Jun 16)',
                                'Dead End Escape (Jun 18)',
                                'True or False: The World (Jun 18)',
                                'Ultimate Wobble Run (Jun 18)',
                                'WordRaid (Jun 18)',
                                "EnjoyUp's 3 in 1 (Jun 19)",
                                'Kiln: Pride Decoration Pack (Jun 19)',
                                'Drunk or Baby (Jun 22)',
                                'Scales of Silence (Jun 22)',
                                'Horse & Horse (Jun 23)',
                                'MAGE (Jun 23)',
                                'Patchkins Party (Jun 23)',
                                'Sloop (Jun 23)',
                                'Guessed It! (Jun 24)',
                                'Coven: All Powerful (Jun 30)',
                                'Drunk Dad Vs Family (Jun 30)',
                                'PiPu Party (Jun 30)',
                                'Runback (Jun 30)',
                                'The Song Of Will (Jun 30)',
                                'Trinity Archetype (Jun 30)',
                                'Turnip Rush (Jun 30)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('At the top of the table again this month is the usual crowd of always-on, single-game channels: '),
                            link('https://twitch.tv/mrllamavod', 'mrllamavod'),
                            text(' with '),
                            em('Diablo II: Resurrected'),
                            text(', '),
                            link('https://twitch.tv/telinha', 'Telinha'),
                            text(' on Fall Guys, and '),
                            link('https://twitch.tv/jackboxplay', 'jackboxplay'),
                            text(' streaming various '),
                            em('Jackbox Party Packs'),
                            text('. A new face in the top ten is '),
                            link('https://twitch.tv/miidouw_', 'Miidouw_'),
                            text(', the French channel whose near-constant '),
                            em('nanos world'),
                            text(" streaming is the engine behind that game's chart-topping stickiness. Audiences on this list are usually thin, but not always: "),
                            link('https://twitch.tv/africaalbion', 'AfricaAlbion'),
                            text(' pairs 122 sessions with an 89-viewer average, streaming '),
                            em('Words On Stream'),
                            text(' to a Portuguese-speaking crowd.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Captures',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '202',
                                    '63',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '201',
                                    '113',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                    '201',
                                    '20',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '201',
                                    '14',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '199',
                                    '31',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '198',
                                    '20',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/africaalbion', 'AfricaAlbion')],
                                    '122',
                                    '89',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                    '97',
                                    '78',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/cabgxaero', 'CABGxAero')],
                                    '96',
                                    '11',
                                    '1',
                                ],
                                [
                                    '10',
                                    [
                                        link('https://twitch.tv/salad_enjoyerr', 'Salad_Enjoyerr'),
                                    ],
                                    '92',
                                    '15',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 captures',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Every name here is a large mainstream channel making a brief appearance, so the interesting question is not who they are but what pulled them in. The common thread is '),
                            em('Meccha Chameleon'),
                            text(': four of the ten biggest audiences came from stars sampling the breakout, including '),
                            link('https://twitch.tv/caseoh_', 'caseoh_'),
                            text(' and '),
                            link('https://twitch.tv/trymacs', 'Trymacs'),
                            text(', who each paired it with the casino sim '),
                            em('The House Always Wins'),
                            text('. The rest gravitated to novelty and game-show formats rather than traditional party staples: '),
                            link('https://twitch.tv/xqc', 'xQc'),
                            text(' drew huge crowds to '),
                            em('Who Wants to Be a Millionaire'),
                            text(' and '),
                            em('Clubhouse Games: 51 Worldwide Classics'),
                            text(', while '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(' and '),
                            link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                            text(' lifted '),
                            em("Liar's Bar"),
                            text('. '),
                            link('https://twitch.tv/jynxzi', 'Jynxzi'),
                            text(' still tops the table on the strength of two '),
                            em('Fall Guys'),
                            text(' streams.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Captures',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                    '70,276',
                                    '2',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                    '55,987',
                                    '4',
                                    '2',
                                ],
                                [
                                    '3',
                                    [
                                        link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                    ],
                                    '38,194',
                                    '2',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                    '33,440',
                                    '4',
                                    '3',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '28,508',
                                    '2',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/xqc', 'xQc')],
                                    '25,089',
                                    '2',
                                    '2',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/alanzoka', 'alanzoka')],
                                    '18,839',
                                    '2',
                                    '2',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/trymacs', 'Trymacs')],
                                    '14,109',
                                    '2',
                                    '2',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/lirik', 'LIRIK')],
                                    '13,358',
                                    '4',
                                    '4',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/zerator', 'ZeratoR')],
                                    '11,249',
                                    '2',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Versatility here comes in recognizable flavors rather than one wide mix. The largest cluster is Nintendo party history: '),
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(' works through the entire '),
                            em('Mario Party'),
                            text(' back catalog, and newcomers to the list like '),
                            link('https://twitch.tv/clarksmadness', 'ClarksMadness'),
                            text(' and '),
                            link('https://twitch.tv/connorcondria20', 'ConnorCondria20'),
                            text(' do the same, rotating '),
                            em('Mario Party'),
                            text(' entries alongside '),
                            em('Mario Kart'),
                            text(' and '),
                            em('Fortune Street'),
                            text('. A second lane is the Jackbox anthology: '),
                            link('https://twitch.tv/mattitude1233', 'Mattitude1233'),
                            text(' logs 24 streams from '),
                            em('Jackbox Party Packs'),
                            text(' and '),
                            link('https://twitch.tv/quarrysea', 'QuarrySea'),
                            text(' works a similar beat. '),
                            em('Codenames'),
                            text(' is another great connector of party streaming, appearing in four of the top ten channels. Of the four new streamers on the list this month, '),
                            link('https://twitch.tv/dumbdog', 'DumbDog'),
                            text(' stands out with an average viewership of 713, more than double that of any other streamer on the list.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Captures',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '16',
                                    '83',
                                    '16',
                                ],
                                [
                                    '2',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '10',
                                    '85',
                                    '66',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '10',
                                    '18',
                                    '235',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/mattitude1233', 'Mattitude1233')],
                                    '7',
                                    '35',
                                    '19',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/quarrysea', 'QuarrySea')],
                                    '7',
                                    '20',
                                    '53',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/dumbdog', 'DumbDog')],
                                    '7',
                                    '19',
                                    '713',
                                ],
                                [
                                    '7',
                                    [
                                        link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                                    ],
                                    '7',
                                    '13',
                                    '313',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/clarksmadness', 'ClarksMadness')],
                                    '7',
                                    '12',
                                    '10',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/saam', 'Saam')],
                                    '7',
                                    '9',
                                    '38',
                                ],
                                [
                                    '10',
                                    [
                                        link('https://twitch.tv/connorcondria20', 'ConnorCondria20'),
                                    ],
                                    '7',
                                    '7',
                                    '10',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('The table has been remarkably steady month to month, so only a few small changes are worth noting in June. Russian was the standout, climbing from sixth to fourth on a 14.3% rise in streams and nudging German and Japanese each down a rank. Its gain leaned on social titles like '),
                            em('Gartic Phone'),
                            text(', which was played more than double any other Russian game. At the edge of the top ten, Arabic climbed into tenth place replacing Polish. Only Malay fell off the complete list of languages this month. Despite only two days of data, nine out of the top ten languages included '),
                            em('Meccha Chameleon'),
                            text(' in their top five Games with Most Streams.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '111,848', '44,853', '20'],
                                ['2', 'Spanish', '15,290', '7,411', '12'],
                                ['3', 'French', '10,463', '3,897', '40'],
                                ['4', 'Russian', '8,924', '3,633', '21'],
                                ['5', 'German', '8,719', '3,991', '37'],
                                ['6', 'Japanese', '8,482', '3,432', '17'],
                                ['7', 'Portuguese', '4,516', '2,113', '32'],
                                ['8', 'Chinese', '3,047', '1,314', '31'],
                                ['9', 'Italian', '1,470', '705', '127'],
                                ['10', 'Arabic', '852', '408', '16'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__english__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '7,907', '4,589'],
                                        ['2', 'Super Smash Bros. Ultimate', '7,442', '2,123'],
                                        ['3', 'Fall Guys', '7,129', '3,615'],
                                        ['4', 'Words On Stream', '6,217', '508'],
                                        ['5', 'MECCHA CHAMELEON', '5,752', '4,711'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        [
                                            '1',
                                            'Clubhouse Games: 51 Worldwide Classics',
                                            '1,630',
                                            '15',
                                        ],
                                        ['2', 'Who Wants to Be a Millionaire', '1,029', '26'],
                                        ['3', 'Keep It Up!', '687', '23'],
                                        ['4', 'Panic Delivery', '316', '16'],
                                        ['5', "Liar's Bar", '292', '368'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '202',
                                            '63',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '202',
                                            '9',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '201',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '199',
                                            '31',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '198',
                                            '20',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                            '70,276',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/caseoh_', 'caseoh_')],
                                            '55,987',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                            ],
                                            '38,194',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/xqc', 'xQc')],
                                            '25,089',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lirik', 'LIRIK')],
                                            '13,358',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__spanish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,752', '1,113'],
                                        ['2', 'Super Smash Bros. Ultimate', '1,652', '592'],
                                        ['3', 'Gartic Phone', '1,485', '472'],
                                        ['4', 'Fall Guys', '1,391', '787'],
                                        ['5', 'MECCHA CHAMELEON', '967', '818'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Party Quiz', '727', '5'],
                                        ['2', 'The House Always Wins', '430', '14'],
                                        ['3', 'Super Bunny Man', '317', '14'],
                                        ['4', 'Just Dance', '314', '18'],
                                        ['5', 'Another Door', '250', '31'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '81',
                                            '22',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/negimilive', 'NegimiLive')],
                                            '55',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/arusonn', 'Arusonn')],
                                            '42',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/shy_guay', 'shy_guay')],
                                            '38',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/exponso', 'Exponso')],
                                            '34',
                                            '31',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '3,902',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mictia00', 'Mictia00')],
                                            '2,194',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vegetta777', 'VEGETTA777')],
                                            '1,937',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/polispol1', 'polispol1')],
                                            '1,096',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/axozer', 'aXoZer')],
                                            '737',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__french__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '2,256', '150'],
                                        ['2', 'Among Us', '995', '595'],
                                        ['3', 'Fall Guys', '864', '412'],
                                        ['4', 'Mario Kart World', '737', '251'],
                                        ['5', 'Super Smash Bros. Ultimate', '729', '251'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Maitre des Fleurs', '1,037', '20'],
                                        ['2', 'Wii Party', '980', '20'],
                                        ['3', 'Backseat Drivers', '320', '6'],
                                        ['4', 'Lycans', '192', '214'],
                                        ['5', 'Overcooked! 2', '182', '46'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '97',
                                            '78',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/barbie_the_queen', 'barbie_the_queen'),
                                            ],
                                            '60',
                                            '56',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '57',
                                            '28',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/ventura_universe', 'Ventura_Universe'),
                                            ],
                                            '57',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aarixxa', 'aarixxa')],
                                            '48',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/zerator', 'ZeratoR')],
                                            '11,249',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mistermv', 'mistermv')],
                                            '9,793',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '4,159',
                                            '11',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/drfeelgood', 'DrFeelgood')],
                                            '1,764',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/anaee', 'Anaee')],
                                            '1,060',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__russian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Gartic Phone', '2,631', '734'],
                                        ['2', 'Jackbox Party Packs', '755', '232'],
                                        ['3', 'MECCHA CHAMELEON', '657', '533'],
                                        ['4', 'Lethal Company', '523', '241'],
                                        ['5', 'Diablo II: Resurrected', '487', '76'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Super Battle Golf', '474', '35'],
                                        ['2', 'Casino', '185', '13'],
                                        ['3', 'Barnyard', '124', '13'],
                                        ['4', 'Pummel Party', '87', '140'],
                                        ['5', 'Mario Kart World', '83', '10'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jackboxplay', 'jackboxplay')],
                                            '201',
                                            '20',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/hc_fm', 'hc_fm')],
                                            '62',
                                            '9',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/irrrrrrrris', 'irrrrrrrris')],
                                            '49',
                                            '28',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/sashatechnic', 'SashaTechnic'),
                                            ],
                                            '49',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/molya_requiem', 'molya_requiem'),
                                            ],
                                            '46',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mazellovvv', 'mazellovvv')],
                                            '6,216',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '4,436',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/c_a_k_e', 'C_a_k_e')],
                                            '2,488',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/alinarinrin', 'AlinaRinRin')],
                                            '1,634',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/fivfiv001', 'fivfiv001')],
                                            '694',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__german__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,489', '793'],
                                        ['2', 'MECCHA CHAMELEON', '657', '532'],
                                        ['3', 'Fall Guys', '657', '344'],
                                        ['4', 'Golf With Your Friends', '577', '376'],
                                        ['5', 'Super Smash Bros. Ultimate', '510', '141'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Monopoly', '139', '15'],
                                        ['2', 'MECCHA CHAMELEON', '136', '657'],
                                        ['3', 'The House Always Wins', '127', '130'],
                                        ['4', 'Hay Day', '114', '25'],
                                        ['5', 'Paddle Paddle Paddle', '72', '17'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '85',
                                            '66',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kastell1974', 'Kastell1974')],
                                            '46',
                                            '34',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/unfurkable61', 'unfurkable61'),
                                            ],
                                            '43',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/karespielt', 'karespielt')],
                                            '26',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/schnubbirella', 'schnubbirella'),
                                            ],
                                            '23',
                                            '18',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '28,508',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/trymacs', 'Trymacs')],
                                            '14,109',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/xthesolutiontv', 'xTheSolutionTV'),
                                            ],
                                            '2,842',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/gronkhtv', 'GronkhTV')],
                                            '1,478',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kdrkitten', 'KDRkitten')],
                                            '1,436',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__japanese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '2,132', '750'],
                                        ['2', 'Super Smash Bros. Ultimate', '754', '222'],
                                        ['3', 'Beat Saber', '644', '146'],
                                        ['4', 'MECCHA CHAMELEON', '438', '389'],
                                        ['5', 'Among Us', '391', '270'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'PICO PARK 2', '472', '18'],
                                        ['2', 'First Class Trouble', '405', '9'],
                                        [
                                            '3',
                                            'Itadaki Street: Dragon Quest & Final Fantasy 30th Anniversary',
                                            '281',
                                            '11',
                                        ],
                                        ['4', 'Earth Defense Force: World Brothers', '101', '9'],
                                        ['5', 'MECCHA CHAMELEON', '88', '438'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sabomkw', 'さっぼさん')],
                                            '54',
                                            '9',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/yzkrnorn', 'メスガキ夜桜')],
                                            '51',
                                            '12',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/natyu_twitch', 'なちゅな')],
                                            '32',
                                            '15',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/takum1rmu', 'おかでぃーです')],
                                            '32',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/gomashiojijii', 'ごましおじじい'),
                                            ],
                                            '32',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/hiiragitsurugi', '柊ツルギ')],
                                            '7,785',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kolu_pen', 'こるぺん')],
                                            '1,662',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/amakipururu', '天鬼ぷるる')],
                                            '1,170',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/murakamisuigun', 'むらまこ')],
                                            '759',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kettuncyukyuhei', 'けっつん')],
                                            '741',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__portuguese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Words On Stream', '396', '52'],
                                        ['2', 'Fall Guys', '392', '111'],
                                        ['3', 'Gartic Phone', '357', '105'],
                                        ['4', 'MECCHA CHAMELEON', '270', '229'],
                                        ['5', 'Among Us', '247', '175'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Trail & Error', '2,572', '7'],
                                        ['2', 'First Class Trouble', '357', '68'],
                                        ['3', 'Chained Beasts', '148', '5'],
                                        ['4', 'Lycans', '103', '7'],
                                        ['5', 'Fall Guys', '64', '392'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '201',
                                            '113',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/africaalbion', 'AfricaAlbion'),
                                            ],
                                            '122',
                                            '89',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/maepistolaa', 'MaePistolaa')],
                                            '48',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vanilian', 'Vanilian')],
                                            '44',
                                            '82',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/joaoxfrango', 'joaoxfrango')],
                                            '36',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/alanzoka', 'alanzoka')],
                                            '18,839',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mount', 'mount')],
                                            '1,448',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/smzinho', 'Smzinho')],
                                            '1,074',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/zanfas_cenegal', 'Zanfas_Cenegal'),
                                            ],
                                            '781',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/impakt', 'impakt')],
                                            '759',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__chinese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '736', '270'],
                                        ['2', 'MECCHA CHAMELEON', '452', '340'],
                                        ['3', 'Mabinogi', '242', '42'],
                                        ['4', 'Goose Goose Duck', '189', '139'],
                                        ['5', 'Party Animals', '143', '93'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Bow-wow Battle', '458', '11'],
                                        ['2', 'Mario Kart World', '311', '24'],
                                        ['3', 'Bunny Guys!', '64', '10'],
                                        ['4', 'Mahjong Soul', '55', '736'],
                                        ['5', 'Rhythm Heaven Fever', '45', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/linadee119', '阿智先生')],
                                            '26',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/news86', 'News86')],
                                            '23',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/u_0624', '小鳥遊ゆり')],
                                            '20',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/daaafuuu', '草莓紅豆大福')],
                                            '20',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/sunsun_pumpkin', '蓀蓀楠瓜')],
                                            '16',
                                            '19',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/p1lepe1el1', '珮蕾')],
                                            '1,840',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/reirei_neon', '澪れい_霓')],
                                            '1,248',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mimiluckying', '獅子丸')],
                                            '1,205',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/moondogs_celestial', '幻月_')],
                                            '1,006',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/dingding248', '丁義')],
                                            '822',
                                            '13',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__italian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '252', '106'],
                                        ['2', 'Among Us', '214', '130'],
                                        ['3', 'Gartic Phone', '108', '51'],
                                        ['4', 'Super Smash Bros. Ultimate', '84', '32'],
                                        ['5', 'MECCHA CHAMELEON', '73', '62'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', "Liar's Bar", '4,347', '15'],
                                        ['2', 'Super Battle Golf', '2,246', '18'],
                                        ['3', 'MECCHA CHAMELEON', '139', '73'],
                                        ['4', 'Just Dance 2026 Edition', '107', '10'],
                                        ['5', 'Mario Kart 8 Deluxe', '46', '37'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '22',
                                            '26',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/nonnogamer70', 'NonnoGamer70'),
                                            ],
                                            '17',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/anivtwitch', 'anivtwitch')],
                                            '16',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/j_papu_03', 'J_Papu_03')],
                                            '13',
                                            '42',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/depeche__', 'Depeche__')],
                                            '13',
                                            '23',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                            '33,440',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '3,360',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/vivalafazza', 'VivaLaFazza')],
                                            '948',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/pesh', 'Pesh')],
                                            '525',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zazzone230', 'Zazzone230')],
                                            '280',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__arabic',
                    title: 'Arabic',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__arabic__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Gartic Phone', '304', '71'],
                                        ['2', 'MECCHA CHAMELEON', '86', '73'],
                                        ['3', 'Fall Guys', '72', '34'],
                                        ['4', 'Codenames', '55', '38'],
                                        ['5', 'Among Us', '52', '28'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__arabic__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Kalak', '34', '5'],
                                        ['2', 'Gartic Show', '29', '11'],
                                        ['3', 'MECCHA CHAMELEON', '24', '86'],
                                        ['4', 'Gartic Phone', '21', '304'],
                                        ['5', 'Codenames', '17', '55'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__arabic__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mody81', 'MODY81')],
                                            '22',
                                            '74',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/8oi_', '8oi_')],
                                            '18',
                                            '33',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/loayksa', 'loayksa')],
                                            '15',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/l7l7', 'L7L7')],
                                            '14',
                                            '30',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/amrou4', 'AMROU4')],
                                            '13',
                                            '11',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__arabic__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dharyfahad', 'dharyfahad')],
                                            '224',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/iz0yi', 'IZ0YI')],
                                            '96',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ho0ud', 'HO0UD')],
                                            '90',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ulualazm', 'UluAlazm')],
                                            '88',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/elsukr', 'Elsukr')],
                                            '77',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('2,328 streams | 156 streamers | 14.9 stickiness | 53 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/miidouw_', 'Miidouw_')],
                                            '97',
                                            '78',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/queenihtv', 'QueenihTV')],
                                            '57',
                                            '28',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/ventura_universe', 'Ventura_Universe'),
                                            ],
                                            '57',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/aarixxa', 'aarixxa')],
                                            '48',
                                            '6',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/adamchico_', 'AdamChico_')],
                                            '47',
                                            '51',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/qassimiento', 'Qassimiento')],
                                            '1,005',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '770',
                                            '34',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '684',
                                            '22',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mowraa', 'Mowraa')],
                                            '375',
                                            '24',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/betr0uni', 'Betr0uni')],
                                            '322',
                                            '39',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('1,751 streams | 166 streamers | 10.5 stickiness | 28 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '52',
                                            '599',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/enteryourinitials', 'EnterYourInitials'),
                                            ],
                                            '34',
                                            '33',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/netherworldarcade', 'netherworldarcade'),
                                            ],
                                            '32',
                                            '63',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '32',
                                            '24',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/whatsbrewing', 'whatsbrewing'),
                                            ],
                                            '32',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '599',
                                            '52',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/netherworldarcade', 'netherworldarcade'),
                                            ],
                                            '63',
                                            '32',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/electricbatarcade', 'ElectricBatArcade'),
                                            ],
                                            '51',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/flipsidememphis', 'flipsidememphis'),
                                            ],
                                            '44',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/raydaypinball', 'raydaypinball'),
                                            ],
                                            '42',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__gartic-phone',
                    title: 'Gartic Phone',
                    collapsible: true,
                    blocks: [
                        p('9,139 streams | 2,871 streamers | 3.2 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__gartic-phone__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '68',
                                            '22',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/sen_927', 'sen_927')],
                                            '57',
                                            '17',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/irrrrrrrris', 'irrrrrrrris')],
                                            '49',
                                            '28',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vanilian', 'Vanilian')],
                                            '37',
                                            '84',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/skywolf272', 'skywolf272')],
                                            '35',
                                            '28',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__gartic-phone__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/sleduck', 'SleDuck')],
                                            '4,717',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/cottontailva', 'CottontailVA'),
                                            ],
                                            '2,934',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/gronkhtv', 'GronkhTV')],
                                            '1,478',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vadeal', 'Vadeal')],
                                            '854',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/murakamisuigun', 'むらまこ')],
                                            '759',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('4,097 streams | 1,448 streamers | 2.8 stickiness | 23 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/raisinnnnnn', 'raisinnnnnn')],
                                            '51',
                                            '24',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '43',
                                            '32',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/takum1rmu', 'おかでぃーです')],
                                            '32',
                                            '7',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/gomashiojijii', 'ごましおじじい'),
                                            ],
                                            '32',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mizarg2', 'mizarg2')],
                                            '30',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/p1lepe1el1', '珮蕾')],
                                            '1,840',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lovelymomo', 'lovelymomo')],
                                            '1,327',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mimiluckying', '獅子丸')],
                                            '1,205',
                                            '8',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/moondogs_celestial', '幻月_')],
                                            '1,006',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/dingding248', '丁義')],
                                            '832',
                                            '12',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__uno',
                    title: 'UNO',
                    collapsible: true,
                    blocks: [
                        p('1,858 streams | 526 streamers | 3.5 stickiness | 22 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__uno__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/calicowboy78', 'CaliCowboy78'),
                                            ],
                                            '17',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mrsdoeeyes', 'MrsDoeEyes')],
                                            '11',
                                            '16',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/imoutopup', 'imoutopup')],
                                            '9',
                                            '34',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bluebrii', 'bluebrii')],
                                            '9',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/romanempire116', 'RomanEmpire116'),
                                            ],
                                            '9',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__uno__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/chibidoki', 'chibidoki')],
                                            '4,269',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ray', 'Ray')],
                                            '3,086',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/cyyuvtuber', 'CyYuVtuber')],
                                            '1,735',
                                            '3',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/axialmatt', 'AxialMatt')],
                                            '558',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/benbestreaming', 'BenbeStreaming'),
                                            ],
                                            '409',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__fall-guys',
                    title: 'Fall Guys',
                    collapsible: true,
                    blocks: [
                        p('11,726 streams | 5,766 streamers | 2 stickiness | 30 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '201',
                                            '113',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/salad_enjoyerr', 'Salad_Enjoyerr'),
                                            ],
                                            '92',
                                            '15',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/chaofanj', 'ChaofanJ')],
                                            '58',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/molya_requiem', 'molya_requiem'),
                                            ],
                                            '46',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vvvombatik', 'vvvombatik')],
                                            '45',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/jynxzi', 'Jynxzi')],
                                            '70,276',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/realzbluewater', 'RealzBlueWater'),
                                            ],
                                            '4,366',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/lospollostv', 'LosPollosTV')],
                                            '3,780',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/edybot', 'EdyBot')],
                                            '570',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/callumfromthecorner', 'CallumFromTheCorner'),
                                            ],
                                            '470',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('6,923 streams | 1,129 streamers | 6.1 stickiness | 13 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '202',
                                            '63',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '80',
                                            '22',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/d2kariya', 'd2kariya')],
                                            '76',
                                            '18',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/devildimlight_ttv', 'DevilDimLight_TTV'),
                                            ],
                                            '69',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/alexislegit', 'AlexIsLegit')],
                                            '69',
                                            '25',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/c_a_k_e', 'C_a_k_e')],
                                            '2,488',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/arex', 'Arex')],
                                            '433',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '418',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kano', 'Kano')],
                                            '358',
                                            '21',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/stealthygolem', 'Stealthygolem'),
                                            ],
                                            '340',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('7,041 streams | 587 streamers | 12 stickiness | 14 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '202',
                                            '9',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '199',
                                            '31',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '198',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '183',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mamabunnz', 'mamabunnz')],
                                            '181',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/leopard', 'Leopard')],
                                            '527',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/piapiufo', 'PiaPiUFO')],
                                            '429',
                                            '11',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/eastahh', 'Eastahh')],
                                            '400',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/emilycc', 'Emilycc')],
                                            '287',
                                            '18',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lightorias', 'Lightorias')],
                                            '205',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('493 streams | 207 streamers | 2.4 stickiness | 52 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/summerskin90', 'summerskin90'),
                                            ],
                                            '14',
                                            '16',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ulises_eric', 'Ulises_Eric')],
                                            '14',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/ratatoscr', 'RATATOscr')],
                                            '11',
                                            '24',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/malikbonneau', 'MalikBonneau'),
                                            ],
                                            '11',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/sierravampire', 'SierraVampire'),
                                            ],
                                            '8',
                                            '71',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/staryuuki', 'Staryuuki')],
                                            '2,799',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,728',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/renrizevods', 'RenRizeVODs')],
                                            '678',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/littlesiha', 'littlesiha')],
                                            '554',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/fems', 'Fems')],
                                            '322',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('11,318 streams | 3,423 streamers | 3.3 stickiness | 13 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Captures', 'Avg Viewers'],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '201',
                                            '14',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/metalvayne08', 'metalvayne08'),
                                            ],
                                            '60',
                                            '39',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/amiibots', 'Amiibots')],
                                            '60',
                                            '9',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/cirrentv', 'CirrenTV')],
                                            '43',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/superdumbnasty', 'SuperDumbNasty'),
                                            ],
                                            '37',
                                            '23',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 captures',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Streamer', 'Avg Viewers', 'Captures'],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/lowtiergod', 'LowTierGod')],
                                            '1,772',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gardman666', 'がーどまん')],
                                            '738',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '576',
                                            '21',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/nkunim', 'Nkunim')],
                                            '529',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/btssmash', 'btssmash')],
                                            '451',
                                            '5',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            blocks: [p('')],
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Weekends bring the most streams but the thinnest audiences on average, while we see the opposite during the week. Saturday led stream counts and Tuesday led average viewers. The most interesting thing this month is, again, the impact '),
                            em('Meccha Chameleon'),
                            text(' has had on Twitch streaming: every weekday drew fewer average streams than in May except Monday and Tuesday, the two days that caught our brief window of the game. Had the game been tracked all month, every day would likely have seen a surge month-over-month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '938', '18'],
                                ['Monday', '895', '26'],
                                ['Tuesday', '928', '34'],
                                ['Wednesday', '747', '17'],
                                ['Thursday', '736', '25'],
                                ['Friday', '865', '19'],
                                ['Saturday', '1,035', '15'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Stream volume peaks in the hours after midnight UTC, the Americas evening, and bottoms out around 09:00 to 10:00 UTC when most of the West is asleep. Audience density follows a different clock: the best-watched hours are 18:00 and 22:00 UTC, when European prime time overlaps the North American afternoon and evening.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,243', '20'],
                                ['01:00', '1,284', '14'],
                                ['02:00', '1,269', '23'],
                                ['03:00', '1,301', '22'],
                                ['04:00', '1,092', '24'],
                                ['05:00', '850', '22'],
                                ['06:00', '712', '12'],
                                ['07:00', '550', '22'],
                                ['08:00', '415', '15'],
                                ['09:00', '400', '13'],
                                ['10:00', '387', '15'],
                                ['11:00', '454', '24'],
                                ['12:00', '516', '23'],
                                ['13:00', '710', '19'],
                                ['14:00', '672', '15'],
                                ['15:00', '766', '19'],
                                ['16:00', '798', '17'],
                                ['17:00', '914', '16'],
                                ['18:00', '1,155', '39'],
                                ['19:00', '1,165', '24'],
                                ['20:00', '1,180', '21'],
                                ['21:00', '1,178', '29'],
                                ['22:00', '1,084', '40'],
                                ['23:00', '1,099', '33'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-06-top-party-games-and-streamers.js.map